import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';

/** Host web component class when the embed section is in full-page layout. */
export const CANVAS_HOST_FULLPAGE_CLASS = 'agentable-canvas-host-fullpage';

export const CANVAS_FULLPAGE_EVENT = 'landi:canvas-fullpage';

export interface CanvasFullPageEventDetail {
  active: boolean;
}

export interface UseCanvasFullscreenResult {
  /** True when the canvas is in host-coordinated full-page layout. */
  isFullscreen: boolean;
  enterFullscreen: () => Promise<void>;
  exitFullscreen: () => Promise<void>;
}

function getShadowHost(el: HTMLElement): HTMLElement | null {
  const root = el.getRootNode();
  if (root instanceof ShadowRoot && root.host instanceof HTMLElement) {
    return root.host;
  }
  return null;
}

function dispatchFullPageEvent(
  host: HTMLElement | null,
  active: boolean,
): void {
  if (!host) return;
  host.dispatchEvent(
    new CustomEvent<CanvasFullPageEventDetail>(CANVAS_FULLPAGE_EVENT, {
      bubbles: true,
      composed: true,
      detail: { active },
    }),
  );
}

/**
 * Full-page engage for embedded canvases: dispatches `landi:canvas-fullpage`
 * on the shadow host so the host page can expand the embed section below its
 * site nav (no document Fullscreen API). Does not remount React — safe for
 * active voice sessions.
 */
export function useCanvasFullscreen(
  rootRef: RefObject<HTMLElement | null>,
): UseCanvasFullscreenResult {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const isActiveRef = useRef(false);

  const enterFullscreen = useCallback(async (): Promise<void> => {
    const root = rootRef.current;
    if (!root || isActiveRef.current) return;

    const host = getShadowHost(root);
    host?.classList.add(CANVAS_HOST_FULLPAGE_CLASS);
    dispatchFullPageEvent(host, true);
    isActiveRef.current = true;
    setIsFullscreen(true);
  }, [rootRef]);

  const exitFullscreen = useCallback(async (): Promise<void> => {
    if (!isActiveRef.current) return;

    const root = rootRef.current;
    const host = root ? getShadowHost(root) : null;
    host?.classList.remove(CANVAS_HOST_FULLPAGE_CLASS);
    dispatchFullPageEvent(host, false);
    isActiveRef.current = false;
    setIsFullscreen(false);
  }, [rootRef]);

  useEffect(() => {
    if (!isFullscreen) return undefined;

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        void exitFullscreen();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isFullscreen, exitFullscreen]);

  useEffect(() => {
    return () => {
      if (isActiveRef.current) {
        const root = rootRef.current;
        const host = root ? getShadowHost(root) : null;
        host?.classList.remove(CANVAS_HOST_FULLPAGE_CLASS);
        dispatchFullPageEvent(host, false);
        isActiveRef.current = false;
      }
    };
  }, [rootRef]);

  return {
    isFullscreen,
    enterFullscreen,
    exitFullscreen,
  };
}
